import { GridColDef } from '@mui/x-data-grid-pro';
import { BillingWeekRecord } from '@api/Trinium/Endpoints/Settlements/types';
import { ParsedDate } from '@api/Trinium/types';
import { negativeAmountClass } from '../gridStyles';

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const dayLabelFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric'
});

export const columns: GridColDef<BillingWeekRecord>[] = [
  { field: 'loadNo', headerName: 'Order #', width: 100 },
  { field: 'customer', headerName: 'Customer', minWidth: 180, flex: 1 },
  { field: 'serviceCode', headerName: 'Service Code', width: 130 },
  { field: 'serviceDescription', headerName: 'Service Description', minWidth: 180, flex: 1 },
  { field: 'container', headerName: 'Container #', width: 140 },
  {
    field: 'chargeAmount',
    headerName: 'Charge Amount',
    width: 140,
    type: 'number',
    valueFormatter: (value: number | undefined) => (value != null ? currencyFormatter.format(value) : ''),
    cellClassName: ({ row }) => (row.chargeAmount < 0 ? negativeAmountClass : '')
  },
  {
    field: 'fullyDeliveredDate',
    headerName: 'Fully Delivered Date',
    width: 160,
    valueGetter: (value: ParsedDate | undefined) => value?.shortDateExtended,
    sortComparator: (a: string | undefined, b: string | undefined) => (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0)
  },
  {
    field: 'invoiceDate',
    headerName: 'Invoice Date',
    width: 130,
    valueGetter: (value: ParsedDate | undefined) => value?.shortDateExtended,
    sortComparator: (a: string | undefined, b: string | undefined) => (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0)
  }
];

/** A single at-a-glance row: `key` identifies the group for click-to-filter, `label` is what the user sees. */
export interface SummaryRow {
  key: string;
  label: string;
  amount: number;
}

export interface BillingWeekSummaries {
  byService: SummaryRow[];
  byDay: SummaryRow[];
  byCustomer: SummaryRow[];
  total: number;
}

export function buildBillingWeekSummaries(records: BillingWeekRecord[]): BillingWeekSummaries {
  const byService = new Map<string, SummaryRow>();
  const byDay = new Map<string, SummaryRow & { unix: number }>();
  const byCustomer = new Map<string, SummaryRow>();
  let total = 0;

  for (const record of records) {
    total += record.chargeAmount;

    const service = byService.get(record.serviceCode) ?? {
      key: record.serviceCode,
      label: `${record.serviceCode} · ${record.serviceDescription}`,
      amount: 0
    };
    service.amount += record.chargeAmount;
    byService.set(record.serviceCode, service);

    if (record.invoiceDate) {
      const day = byDay.get(record.invoiceDate.date) ?? {
        key: record.invoiceDate.date,
        label: dayLabelFormatter.format(record.invoiceDate.unix),
        amount: 0,
        unix: record.invoiceDate.unix
      };
      day.amount += record.chargeAmount;
      byDay.set(record.invoiceDate.date, day);
    }

    const customer = byCustomer.get(record.customerId) ?? {
      key: record.customerId,
      label: record.customer,
      amount: 0
    };
    customer.amount += record.chargeAmount;
    byCustomer.set(record.customerId, customer);
  }

  return {
    byService: [...byService.values()].sort((a, b) => b.amount - a.amount),
    byDay: [...byDay.values()].sort((a, b) => a.unix - b.unix),
    byCustomer: [...byCustomer.values()].sort((a, b) => b.amount - a.amount),
    total
  };
}
